<template>
  <Container>
    <template v-slot:header>Banned Domains</template>
    <template v-slot:content>
      <section class="bg-white shadow rounded-lg pt-8 mb-6 px-4 sm:px-6">
        <div
          class="flex flex-col sm:flex-row justify-between items-center mb-4"
        >
          <h2 class="text-center sm:text-left text-lg text-gray-900">
            Banned domains
          </h2>
          <button
            type="button"
            class="button-submit mt-3 sm:mt-0"
            @click="showImport = true"
          >
            <UploadIcon class="h-5 inline mr-1" />
            Bulk import CSV
          </button>
        </div>
        <p class="text-sm text-gray-500 mb-4">
          Accounts whose email domain is on this list cannot sign up or sign in.
          Subdomains of a banned domain are banned too.
        </p>
        <Form @submit="onSubmit" v-slot="{ errors }" class="space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-start">
            <div class="px-6 py-1 text-sm text-gray-500 text-left input-cell">
              <Field
                id="domain"
                name="domain"
                type="text"
                v-model="domain"
                placeholder="Domain (e.g. example.com)"
                rules="required"
                class="form-field-no-icon w-full"
                @input="clearError()"
              />
              <p class="text-red-500 text-sm mt-1">
                {{ capitalize(errors.domain) }}
              </p>
            </div>
            <div
              class="px-6 py-1 text-sm text-gray-500 text-left input-cell flex-grow"
            >
              <Field
                id="reason"
                name="reason"
                type="text"
                v-model="reason"
                placeholder="Reason (optional)"
                class="form-field-no-icon w-full"
                @input="clearError()"
              />
              <p class="text-red-500 text-sm mt-1">{{ errors.reason }}</p>
            </div>
            <button
              type="submit"
              class="button-submit mt-1 h-9 mx-6 sm:mx-0"
              :disabled="onLoad"
            >
              Add
            </button>
          </div>
        </Form>
        <BannedDomainsTable
          :data="domainData"
          :paginationData="paginationData"
          :searchKeyword="urlParams.search"
          :onPageChange="onPageChange"
          :onSearch="onSearch"
        />
      </section>
      <BannedDomainsImportModal
        v-model:open="showImport"
        @imported="fetchBannedDomains"
      />
    </template>
  </Container>
</template>

<script>
import Container from "@/components/common/Container";
import BannedDomainsTable from "@/components/admin/banned-domains/BannedDomainsTable";
import BannedDomainsImportModal from "@/components/admin/banned-domains/BannedDomainsImportModal";
import apiMixin from "@/mixins/apiMixin";
import formMixin from "@/mixins/formMixin";
import eventBus from "@/utils/eventBus";
import { ref, reactive } from "vue";
import { Field, Form } from "vee-validate";
import { UploadIcon } from "@heroicons/vue/outline";

export default {
  name: "BannedDomains",
  mixins: [apiMixin, formMixin],
  components: {
    Container,
    BannedDomainsTable,
    BannedDomainsImportModal,
    Field,
    Form,
    UploadIcon,
  },
  beforeMount() {
    eventBus.on("update-banned-domains", this.fetchBannedDomains);
  },
  beforeUnmount() {
    eventBus.off("update-banned-domains", this.fetchBannedDomains);
  },
  setup() {
    const domainData = ref([]);
    const paginationData = reactive({});
    const urlParams = reactive({ page: 1, search: "" });

    return {
      domainData,
      paginationData,
      urlParams,
      domain: ref(""),
      reason: ref(""),
      onLoad: ref(false),
      showImport: ref(false),
    };
  },
  methods: {
    async onSubmit(values, actions) {
      this.onLoad = true;
      const response = await this.handleSubmissionErrors(
        () =>
          this.$alfredService.bannedDomainAdd({
            domain: values.domain,
            reason: values.reason || "",
          }),
        { formActions: actions }
      );
      this.onLoad = false;
      if (response) {
        eventBus.trigger("notify", {
          notification: {
            group: "top-right",
            text: "Domain banned.",
            type: "success",
          },
        });
        this.domain = "";
        this.reason = "";
        actions.resetForm();
        await this.fetchBannedDomains();
      }
    },
    clearError() {
      this.onLoad = false;
    },
    capitalize(string) {
      if (string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
      return string;
    },
    async fetchBannedDomains() {
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () =>
          await this.$alfredService.bannedDomainsGet(this.urlParams),
      });
      if (response) {
        this.domainData = response.results || [];
        this.paginationData = {
          count: response.count,
          next: response.next,
          previous: response.previous,
        };
      }
    },
    async onPageChange(page) {
      this.urlParams.page = page;
      await this.fetchBannedDomains();
    },
    async onSearch(keyword) {
      this.urlParams.search = keyword;
      this.urlParams.page = 1;
      await this.fetchBannedDomains();
    },
  },
};
</script>

<style scoped>
.input-cell {
  min-width: 200px;
}
</style>
